import { env } from "@mnk/frontend/config/env";
import { trpc } from "@mnk/frontend/lib/trpc";
import type { Drawing } from "@mnk/shared/models/drawing";
import type { DrawingImage } from "@mnk/shared/models/drawingImage";
import { createIcJCTInfo } from "@mnk/shared/models/icJCTInfo";
import { createInformationTable } from "@mnk/shared/models/informationTable";
import type { Project } from "@mnk/shared/models/project";
import { useCallback, useEffect, useRef, useState } from "react";
import { A4_HEIGHT, A4_WIDTH } from "../const";
import { useBackgroundImage } from "./useBackgroundImage";
import { useFlagRowsAndActions } from "./useFlagRowsAndActions";
import { useHataage } from "./useHataage";
import { useIcJCT } from "./useIcJCT";
import { useInformationTable } from "./useInformationTable";

interface Props {
  zoom: number;
  project: Project;
  drawing: Drawing;
  currentIndex: number;
  totalCount: number;
  currentDrawingImage: DrawingImage;
  quantityId: string;
  officeName: string;
  showFlag: boolean;
  saveImage?: (dataURL: string, index: number) => void;
  autoCapture?: boolean;
}

export const DrawingArea = ({
  zoom,
  project,
  drawing,
  currentIndex,
  totalCount,
  currentDrawingImage,
  quantityId,
  officeName,
  showFlag,
  saveImage,
  autoCapture,
}: Props) => {
  const [renderCompleted, setRenderCompleted] = useState(false);
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const abortControllerRef = useRef<AbortController | null>(null);
  const isRenderingRef = useRef(false);
  const {
    hataageItems,
    setHataageItems,
    isLoading: isLoadingHataage,
  } = useFlagRowsAndActions(quantityId, currentDrawingImage);

  const [draggedItemId, setDraggedItemId] = useState<string | null>(null);
  const [dragOffset, setDragOffset] = useState<{ x: number; y: number } | null>(null);

  const updateFlagRowPosition = trpc.updateFlagRowPosition.useMutation();

  const createInfoTable = trpc.createInformationTable.useMutation();
  const updateInfoTablePosition = trpc.updateInformationTablePosition.useMutation();
  const getInfoTable = trpc.findByDrawingImageIdAndProjectId.useQuery({
    drawingImageId: currentDrawingImage.id,
    projectId: project.id,
  });

  const trpcCreateIcJCTInfo = trpc.createIcJCTInfo.useMutation();
  const updateIcJCTInfoPosition = trpc.updateIcJCTInfoPosition.useMutation();
  const getIcJCTInfo = trpc.getIcJCTInfo.useQuery({
    drawingImageId: currentDrawingImage.id,
    quantityId,
  });

  const isLoadingInfoTable = getInfoTable.isFetching;
  const isLoadingIcJCTInfo = getIcJCTInfo.isFetching;

  const utils = trpc.useUtils();

  const { drawBackgroundImage } = useBackgroundImage();
  const {
    drawInformationTable,
    getInformationTableBounds,
    handleDragStart: handleInformationTableDragStart,
    handleDragMove: handleInformationTableDragMove,
    handleDragEnd: handleInformationTableDragEnd,
    isDragging: isInformationTableDragging,
    dragState,
  } = useInformationTable();

  const { drawHataage, isPointInHataage } = useHataage();

  const {
    IcJCTItems,
    drawIcJCT,
    getIcJCTInfoBounds,
    handleDragStart: handleIcJCTDragStart,
    handleDragMove: handleIcJCTDragMove,
    handleDragEnd: handleIcJCTDragEnd,
    isDragging: isIcJCTDragging,
    dragState: icJCTDragState,
    isLoading: isLoadingIcJCT,
  } = useIcJCT(quantityId, currentDrawingImage);

  // 保存済みのインデックスを追跡して重複保存を防ぐ
  const savedIndexRef = useRef<Set<number>>(new Set());

  useEffect(() => {
    if (!renderCompleted || !autoCapture || !saveImage) return;

    // 既に保存済みのインデックスはスキップ
    if (savedIndexRef.current.has(currentIndex)) {
      setRenderCompleted(false);
      return;
    }

    const canvas = canvasRef.current;
    if (!canvas) return;
    const dataURL = canvas.toDataURL();

    // 保存済みとしてマーク
    savedIndexRef.current.add(currentIndex);
    saveImage(dataURL, currentIndex);
    setRenderCompleted(false);
    const ctx = canvas.getContext("2d");
    if (!ctx) return;

    // キャンバスをクリア
    ctx.clearRect(0, 0, canvas.width, canvas.height);
  }, [renderCompleted, autoCapture, saveImage, currentIndex]);

  // キャンバスの描画を更新
  const updateCanvas = useCallback(async () => {
    if (!canvasRef.current) return;

    // 既存の描画処理をキャンセル
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }

    // 新しいAbortControllerを作成
    abortControllerRef.current = new AbortController();
    const signal = abortControllerRef.current.signal;

    // 描画中フラグを設定
    isRenderingRef.current = true;

    try {
      const canvas = canvasRef.current;
      const ctx = canvas.getContext("2d");
      if (!ctx) return;

      // キャンバスをクリア
      ctx.clearRect(0, 0, canvas.width, canvas.height);

      // 背景を白で塗りつぶす
      ctx.fillStyle = "#FFFFFF";
      ctx.fillRect(0, 0, canvas.width, canvas.height);

      // 背景画像を描画（最下層）
      const src = `${env.DRAWING_BASE_URL}/${currentDrawingImage.fileName}`;
      await drawBackgroundImage(ctx, { src }, canvas.width, signal);

      // showFlagがtrueの場合のみ、その他の要素を描画
      if (showFlag) {
        // 旗揚げを描画
        for (const item of hataageItems) {
          if (signal.aborted) return;
          await drawHataage({
            ctx,
            text: { lines: item.lines },
            position: item.position,
            kiloPost: item.kiloPost,
            config: item.config,
            modifiedKiloPost: item.modifiedKiloPost,
            placeType: item.placeType,
            roadType: item.roadType,
            dates: item.dates,
          });
        }

        if (signal.aborted) return;
        await drawIcJCT({
          ctx,
          data: {
            icJCTItems: IcJCTItems,
            position: {
              x: getIcJCTInfo.data?.positionX ?? 20,
              y: getIcJCTInfo.data?.positionY ?? 2080 - 30 * IcJCTItems.length,
            },
          },
        });

        // 情報テーブルを描画（最上層）
        if (signal.aborted) return;
        await drawInformationTable({
          ctx,
          data: {
            project,
            currentIndex,
            totalCount,
            drawing,
            drawingImage: currentDrawingImage,
            officeName,
            position: {
              x: getInfoTable.data?.positionX ?? 2600,
              y: getInfoTable.data?.positionY ?? 1810,
            },
          },
        });
      }

      // 描画完了を1フレーム後に保証
      if (signal.aborted) return;
      await new Promise(requestAnimationFrame);
      setRenderCompleted(true);
    } catch (error) {
      if (error instanceof Error && error.name === "AbortError") {
        // キャンセルされた場合は何もしない
        return;
      }
      console.error("描画中にエラーが発生しました:", error);
    } finally {
      isRenderingRef.current = false;
    }
  }, [
    currentIndex,
    drawing,
    currentDrawingImage,
    hataageItems,
    project,
    totalCount,
    drawHataage,
    drawInformationTable,
    drawBackgroundImage,
    IcJCTItems,
    drawIcJCT,
    showFlag,
    officeName,
    getInfoTable.data,
    getIcJCTInfo.data,
  ]);

  // キャンバスの描画を更新（依存関係が変更されたときに一度だけ実行）
  useEffect(() => {
    let mounted = true;
    let timeoutId: NodeJS.Timeout;

    const update = async () => {
      if (!mounted || isRenderingRef.current) return;

      // autoCaptureモードの場合は、すべてのデータのロードが完了するまで待機
      if (autoCapture) {
        const isDataLoading = isLoadingHataage || isLoadingIcJCT || isLoadingInfoTable || isLoadingIcJCTInfo;
        if (isDataLoading) {
          return;
        }
      }

      // 前の描画が完了するまで少し待機（デバウンス）
      await new Promise((resolve) => {
        timeoutId = setTimeout(resolve, 50);
      });

      if (!mounted) return;
      await updateCanvas();
    };

    // 依存関係が変更されたときに一度だけ描画を実行
    update();

    return () => {
      mounted = false;
      clearTimeout(timeoutId);
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
    };
  }, [updateCanvas, autoCapture, isLoadingHataage, isLoadingIcJCT, isLoadingInfoTable, isLoadingIcJCTInfo]);

  // マウスイベントの処理
  const handleMouseDown = (e: React.MouseEvent<HTMLCanvasElement>) => {
    if (!canvasRef.current) return;

    const rect = canvasRef.current.getBoundingClientRect();
    const x = (e.clientX - rect.left) / zoom;
    const y = (e.clientY - rect.top) / zoom;

    // 旗揚げをチェック（逆順で検索）
    for (let i = hataageItems.length - 1; i >= 0; i--) {
      const item = hataageItems[i];
      if (isPointInHataage({ x, y }, { lines: item.lines }, item.position, item.config?.lineWidth)) {
        // 他の旗揚げと重なっているかチェック
        let isOverlapped = false;
        for (let j = i + 1; j < hataageItems.length; j++) {
          const topItem = hataageItems[j];
          if (isPointInHataage({ x, y }, { lines: topItem.lines }, topItem.position, topItem.config?.lineWidth)) {
            isOverlapped = true;
            break;
          }
        }
        if (!isOverlapped) {
          setDraggedItemId(item.id);
          // クリック位置と旗揚げ位置のオフセットを保存
          setDragOffset({
            x: x - item.position.x,
            y: y - item.position.y,
          });
          return;
        }
      }
    }

    // 情報テーブルをチェック
    const infoTableBounds = getInformationTableBounds({
      x: getInfoTable.data?.positionX ?? 2600,
      y: getInfoTable.data?.positionY ?? 1810,
    });
    if (
      x >= infoTableBounds.x &&
      x <= infoTableBounds.x + infoTableBounds.width &&
      y >= infoTableBounds.y &&
      y <= infoTableBounds.y + infoTableBounds.height
    ) {
      handleInformationTableDragStart({ x, y });
      return;
    }

    // IcJCTテーブルをチェック
    if (IcJCTItems.length > 0) {
      const icJCTInfoBounds = getIcJCTInfoBounds({
        x: getIcJCTInfo.data?.positionX ?? 20,
        y: getIcJCTInfo.data?.positionY ?? 2080 - 30 * IcJCTItems.length,
      });

      if (
        x >= icJCTInfoBounds.x &&
        x <= icJCTInfoBounds.x + icJCTInfoBounds.width &&
        y >= icJCTInfoBounds.y &&
        y <= icJCTInfoBounds.y + icJCTInfoBounds.height
      ) {
        handleIcJCTDragStart({ x, y });
        return;
      }
    }
  };

  const handleMouseMove = (e: React.MouseEvent<HTMLCanvasElement>) => {
    if (!canvasRef.current) return;

    const rect = canvasRef.current.getBoundingClientRect();
    const x = (e.clientX - rect.left) / zoom;
    const y = (e.clientY - rect.top) / zoom;

    if (draggedItemId && dragOffset) {
      const item = hataageItems.find((item) => item.id === draggedItemId);
      if (!item) return;

      // 旗揚げの位置を更新（オフセットを考慮）
      setHataageItems((prevItems) => {
        return prevItems.map((item) => {
          if (item.id === draggedItemId) {
            return {
              ...item,
              position: {
                x: item.position.x,
                y: y - dragOffset.y,
              },
            };
          }
          return item;
        });
      });
    } else if (isInformationTableDragging) {
      handleInformationTableDragMove({ x, y });
    } else if (isIcJCTDragging) {
      handleIcJCTDragMove({ x, y });
    }

    // ドラッグ中は再描画を要求
    if (!!draggedItemId || isInformationTableDragging || isIcJCTDragging) {
      updateCanvas();
    }
  };

  const handleMouseUp = () => {
    if (draggedItemId) {
      const item = hataageItems.find((item) => item.id === draggedItemId);
      if (!item) return;
      updateFlagRowPosition.mutateAsync({
        id: item.id.replace("modified-", ""),
        drawingImageId: currentDrawingImage.id,
        positionY: item.position.y,
      });
      setDraggedItemId(null);
      setDragOffset(null);
    } else if (isInformationTableDragging) {
      handleInformationTableDragEnd();
      if (!getInfoTable.data) {
        // 情報テーブルが存在しない場合は作成
        createInfoTable
          .mutateAsync(
            createInformationTable({
              projectId: project.id,
              drawingImageId: currentDrawingImage.id,
              positionX: dragState?.currentPosition.x ?? 2600,
              positionY: dragState?.currentPosition.y ?? 1810,
            }),
          )
          .then(() => {
            utils.findByDrawingImageIdAndProjectId.invalidate();
          });
      } else {
        // 既存の情報テーブルの位置を更新
        updateInfoTablePosition
          .mutateAsync({
            id: getInfoTable.data.id,
            positionX: dragState?.currentPosition.x ?? 2600,
            positionY: dragState?.currentPosition.y ?? 1810,
          })
          .then(() => {
            utils.findByDrawingImageIdAndProjectId.invalidate();
          });
      }
    } else if (isIcJCTDragging) {
      handleIcJCTDragEnd();
      if (!getIcJCTInfo.data) {
        // IcJCTテーブルが存在しない場合は作成
        trpcCreateIcJCTInfo
          .mutateAsync(
            createIcJCTInfo({
              quantityId: quantityId,
              drawingImageId: currentDrawingImage.id,
              positionX: icJCTDragState?.currentPosition.x ?? 20,
              positionY: icJCTDragState?.currentPosition.y ?? 1850,
            }),
          )
          .then(() => {
            utils.getIcJCTInfo.invalidate();
          });
      } else {
        // 既存のIcJCTテーブルの位置を更新
        updateIcJCTInfoPosition
          .mutateAsync({
            id: getIcJCTInfo.data.id,
            positionX: icJCTDragState?.currentPosition.x ?? 20,
            positionY: icJCTDragState?.currentPosition.y ?? 1850,
          })
          .then(() => {
            utils.getIcJCTInfo.invalidate();
          });
      }
    }
  };

  return (
    <canvas
      ref={canvasRef}
      width={A4_WIDTH}
      height={A4_HEIGHT}
      className="bg-white shadow"
      style={{
        width: `${A4_WIDTH * zoom}px`,
        height: `${A4_HEIGHT * zoom}px`,
        cursor: showFlag && (!!draggedItemId || isInformationTableDragging || isIcJCTDragging) ? "grabbing" : "grab",
      }}
      onMouseDown={showFlag ? handleMouseDown : undefined}
      onMouseMove={showFlag ? handleMouseMove : undefined}
      onMouseUp={showFlag ? handleMouseUp : undefined}
      onMouseLeave={showFlag ? handleMouseUp : undefined}
    />
  );
};
